import { Typography } from '@mui/material';
import { BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type {
  ResourceType,
  Condition,
  BackupPolicyType,
} from '@repo/api-gw-sdk';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useState } from 'react';

import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import {
  id,
  resourceId,
  resourceType,
} from '@/components/queryBuilder/properties';
import { ColumnSelector } from '@/components/table/columnsPopup';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import {
  createCombineCondition,
  isAnAllCondition,
} from '@/types/advanceFilter';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';

import { InventoryListTable } from '../../inventory/inventoryListTable';

const useCustomPolicySupportedTypes = (
  policyType: BackupPolicyType,
  supportedTypes?: ResourceType[]
): Condition[] => {
  const availableTypes = useBackupPolicyAvailableTypes()[policyType];
  const types = supportedTypes?.length ? supportedTypes : availableTypes;
  if (!types?.length) {
    return [];
  }
  return [
    {
      property: resourceType.name,
      operator: ConditionOperatorEnum.In,
      value: types,
    },
  ];
};

export const BackupPolicyResourcesList = (
  props: StepProps<BackupPolicyEditState>
) => {
  const baseCondition = useBuildCondition(props);

  return (
    <>
      <StepContainer
        stepperLabels={props.stepperLabels}
        stepperIndex={1}
        onBackClick={props.back}
        canGoNext={() => true}
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <BackupPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <Typography className='text-center leading-5 my-[20px]'>
          These are the resources that meet the conditions you set. Review the
          list to make sure it contains the resources you expected.
        </Typography>
        <InventoryTable query={baseCondition} />
      </StepContainer>
    </>
  );
};

const InventoryTable = (props: { query?: Condition }) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: id.name, desc: false },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  return (
    <InventoryListTable
      isMultiSelect={false}
      query={props.query}
      showFiltersBar={false}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      onSortingChange={(id, desc) => setSorting([{ id, desc }])}
      paginationBarActions={[ColumnSelector]}
    />
  );
};

const backupStatusNotInCondition = createBackupStatusNotInCondition([
  BackupStatus.ExcludedFromBackup,
  BackupStatus.InitialClassification,
  BackupStatus.Terminated,
  BackupStatus.Disconnected,
  BackupStatus.Unsupported,
  BackupStatus.LimitExceeded,
]);

const useBuildCondition = (props: StepProps<BackupPolicyEditState>) => {
  const resourceTypesCondition = useCustomPolicySupportedTypes(
    props.wizardState.policyType,
    props.wizardState.resourceTypes
  );

  const baseCondition = createCombineCondition([
    ...resourceTypesCondition,
    backupStatusNotInCondition,
  ]);

  const excludeCondition = {
    operator: ConditionOperatorEnum.NotIn,
    property: resourceId.name,
    value: props.wizardState.exclude,
  };

  const includeCondition = {
    operator: ConditionOperatorEnum.In,
    property: resourceId.name,
    value: props.wizardState.include,
  };

  if (props.wizardState.resourcesScopeType === 'allIncluded') {
    if (props.wizardState.exclude?.length) {
      baseCondition.conditions!.push(excludeCondition);
    }

    return baseCondition;
  }

  if (props.wizardState.resourcesScopeType === 'allExcluded') {
    baseCondition.conditions!.push(includeCondition);
    return baseCondition;
  }

  const userConditions =
    !props.wizardState.condition ||
    isAnAllCondition(props.wizardState.condition)
      ? []
      : [props.wizardState.condition];

  if (props.wizardState.include?.length) {
    baseCondition.conditions!.push(
      userConditions.length
        ? createCombineCondition(
            [...userConditions, includeCondition],
            ConditionOperatorEnum.Or
          )
        : includeCondition
    );
  } else {
    baseCondition.conditions!.push(...userConditions);
  }

  if (props.wizardState.exclude?.length) {
    baseCondition.conditions!.push(excludeCondition);
  }

  return baseCondition;
};
